import assert from 'assert';
import clsx from 'clsx';
import Link from 'next/link';
import { LinkIcon } from '@heroicons/react/24/outline';
import { useCopyLink } from '../copy-link';
import { FlexCentered, FlexRow } from '../layout';
import { PopOver, usePopOver } from '../tool-tip';

export function Pill({ url, onClick, selected = false, className, children }:  {
  url?:       string,
  onClick?:   () => void,
  selected?:  boolean,
  className?: string,
  children:   React.ReactNode
}) {
  assert(!(url && onClick), 'Pill: url and onClick are mutually exclusive.');
  assert(  url || onClick,  'Pill: url or onClick must be defined.'        );

  return <>
    {url && <>
      <Link href={url} prefetch={false} className={`
        text-center p-3 py-2 block mr-1
        rounded-md text-sm font-semibold
        shadow-sm ring-1 ring-inset ring-gray-100 border
        ${selected ? 'cursor-default bg-blue-50 border-blue-300 text-gray-800 ring-gray-400/50'
                  : 'text-gray-700 cursor-pointer hover:bg-blue-50/50 hover:border-blue-100 hover:ring-gray-200'}
        ${className}
      `}>
        {children}
      </Link>
    </>}
    {onClick && <>
      <div onClick={onClick} className={`
        text-center p-3 py-2 block mr-1
        rounded-md text-sm font-semibold
        shadow-sm ring-1 ring-inset ring-gray-100 border
        ${selected ? 'cursor-default bg-blue-50 border-blue-300 text-gray-800 ring-gray-400/50'
                  : 'text-gray-700 cursor-pointer hover:bg-blue-50/50 hover:border-blue-100 hover:ring-gray-200'}
        ${className}
      `}>
        {children}
      </div>
    </>}
  </>;
}

export function Button({ url, onClick, className, children }: {
  url?:     string,
  onClick?: () => void,
  className?: string,
  children: React.ReactNode
}) {
  assert(!(url && onClick), 'Button: url and onClick are mutually exclusive.');
  assert(  url || onClick,  'Button: url or onClick must be defined.'        );

  return <>
    {url && <>
      <Link href={url} prefetch={false} className={`
        font-medium text-center p-16 py-4 ring-outset ring-gray-400/50 ring-2
        bg-gray-200/75 border border-bright-blue text-gray-600
        hover:bg-gray-300 hover:border-gray-800 hover:text-gray-800
        block cursor-pointer rounded-xl
        ${className}
      `}>
        {children}
      </Link>
    </>}
    {onClick && <>
      <div onClick={onClick} role="button" className={`
        font-medium text-center p-16 py-4 ring-outset ring-gray-400/50 ring-2
        bg-gray-200/75 border border-bright-blue text-gray-600
        hover:bg-gray-300 hover:border-gray-800 hover:text-gray-800
        block cursor-pointer rounded-xl
        ${className}
      `}>
        {children}
      </div>
    </>}
  </>;
}
export function PrimaryButton({ url, onClick, className, children, enabled }: {
  url?:       string,
  onClick?:   () => void,
  className?: string,
  children:   React.ReactNode,
  enabled?:   boolean
}) {
  assert(!(url && onClick), 'PrimaryButton: url and onClick are mutually exclusive.');
  assert(  url || onClick,  'PrimaryButton: url or onClick must be defined.'        );

  const enabled_ = enabled === undefined ? true : enabled;
  return <>
    {enabled_ && url && <Link href={url} prefetch={false} className={clsx(`
      font-medium text-center p-4 py-4 ring-outset ring-bright-blue ring-2
      bg-bright-blue text-white
      block rounded-xl
      cursor-pointer hover:ring-blue-800`,
      className
    )}>
      {children}
    </Link>}
    {enabled_ && onClick && <div role="button" onClick={onClick} className={clsx(`
      font-medium text-center p-4 py-4 ring-outset ring-bright-blue ring-2
      bg-bright-blue text-white
      block rounded-xl
      cursor-pointer hover:ring-blue-800`,
      className
    )}>
      {children}
    </div>}
    {!enabled_ && <div role="button" aria-disabled className={clsx(`
      font-medium text-center p-4 py-4 ring-outset ring-pale-blue ring-2
      block rounded-xl bg-pale-blue text-white`,
      className
    )}>
      {children}
    </div>}
  </>;
}
export function LinkButton({ url, onClick, className, children, enabled, targetParent, scroll = true }: {
  url?:          string,
  onClick?:      () => void,
  className?:    string,
  children:      React.ReactNode,
  enabled?:      boolean,
  targetParent?: boolean,
  scroll?:       boolean,
}) {
  assert(!(url && onClick), 'LinkButton: url and onClick are mutually exclusive.');
  assert(  url || onClick,  'LinkButton: url or onClick must be defined. '       );

  assert(!(targetParent && !url), 'LinkButton: targetParent requires url.');

  assert(scroll || !onClick, 'LinkButton: requires url to be defined if scroll is false.');

  const enabled_ = enabled === undefined ? true : enabled;
  return <>
    {enabled_ && onClick && <div onClick={onClick} className={`cursor-pointer text-bright-blue hover:text-bright-blue inline-block ${className}`} role="button">
      {children}
    </div>}
    {enabled_ && url && <Link prefetch={false} target={targetParent ? '_parent' : '_self'} href={url} scroll={scroll} className={`cursor-pointer text-bright-blue hover:text-bright-blue inline-block ${className}`}>
      {children}
    </Link>}
    {!enabled_ && <button disabled className={`text-gray-200 inline-block ${className}`}>
      {children}
    </button>}
  </>;
}

export function TabButton({ url, children, selected, onClick }: React.PropsWithChildren<{
  url?:          string,
  onClick?:      () => void,
  selected?:     boolean,
}>) {
  assert(!(url && onClick), 'TabButton: url and onClick are mutually exclusive.');
  assert(  url || onClick,  'TabButton: url or onClick must be defined. '       );

  return <>
    <LinkButton
      url={url}
      onClick={onClick}
      className={`text-bright-blue text-label-lg w-24 h-12 border-b-2 ${ selected ? "border-bright-blue" : "border-transparent"}`}
    >
      <FlexCentered>
        {children}
      </FlexCentered>
    </LinkButton>
  </>;
}

export function CopyLinkButton({ url, clipboardItem, className }: {
  url?:           string,
  clipboardItem?: ClipboardItem
  className?:     string,
}) {
  const popover = usePopOver();
  const { onCopyAsync } = useCopyLink({ url, clipboardItem });

  const copy = async () => {
    await onCopyAsync();
    popover.show();
    setTimeout(popover.hide, 3000);
  }

  return <>
    <PopOver content="Copied!" open={popover.open}>
      <button onClick={copy}>
        <FlexRow items="center" className={clsx(className, "text-bright-blue text-label-lg gap-2")}>
          <LinkIcon className="w-5 h-5" />
          Copy Link
        </FlexRow>
      </button>
    </PopOver>
  </>;
}
